import { Box, TextField, Tooltip, Autocomplete } from "@mui/material";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Agency, AgencyId, GetAgency } from "../../../reducers/states/agency";
import { OtherAgency } from "../../../reducers/states/agency/agencies";
import { useSelector } from "react-redux";
import { IRootReducer } from "../../../reducers";
import { useUserAgencies } from "../../../hooks/agencies";

interface ViewEditAgencyAutocompleteProps {
  id?: string;
  label?: string;
  characterLimit?: number;
  disableShrinkLabel?: boolean
  enableOther?: boolean;
  isEdit: boolean;
  required?: boolean;
  size?: "small" | "medium";
  tooltip?: string;
  value: string;
  handleInputChange: (newValue: AgencyId | null, field: string) => void;
}

const ViewEditAgencyAutocomplete: FC<ViewEditAgencyAutocompleteProps> = (props) => {
  const [value, setValue] = useState<{ id: string, label: string } | string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const agencyStore = useSelector((state: IRootReducer) => state.agencyReducer); // perhaps pass this in as an array of agencies instead? Or, my useAgencies hook is a good idea?
  const userAgencies = useUserAgencies(); // todo: jcavner: possibly use just agencyStore instead of also using agenciesHook.

  const getAgencyLabel = (agency: Agency): string => {
    return agency.name === agency.abbreviation ? agency.name : `${agency.name} - (${agency.abbreviation})`;
  };

  const agencies = props.enableOther ? userAgencies.concat([OtherAgency]) : userAgencies;
  const options = agencies.map((a) => ({
    id: a.id as string,
    label: getAgencyLabel(a),
  }));

  const getOptionLabel = useCallback((option: { id: string, label: string } | string): string => {
    const value = typeof option === "object" ? option.id : option;
    const agency = GetAgency(agencyStore.agenciesById, value);
    return agency ? getAgencyLabel(agency) : value;
  }, [agencyStore.agenciesById])

  const getValue = useCallback((value: string): { id: string, label: string } | null | string => {
    // The saved values are either an AgencyId, AgencyAbbr, or custom "other" string.
    // When it's an AgencyId, GetAgency() is the way of mapping that to an agency object, to get its label.
    const agency = GetAgency(agencyStore.agenciesById, value);
    // GetAgency() will return undefined if the value was a saved custom string, or wasn't mapped to an agency,
    // in which case we just return the value as is, unless it's an empty string or undefined, then we return null     
    // todo: jcavner: This is where it's changing from saving abbr to saving ID. Need to make sure that's going to be ok/handled everywhere.
    const valueOut = agency ? { id: agency?.id || value, label: getOptionLabel(value) } : value || null;

    return valueOut;
  }, [agencyStore.agenciesById, getOptionLabel])

  useEffect(() => {
    const newValue = getValue(props.value);

    // Make sure value is never set to undefined or an empty string (use null instead).
    // When props.enableOther is false, an empty string will cause this console warning:
    // MUI: The value provided to Autocomplete is invalid. None of the options match with `""`.
    // undefined will cause a console error about switching between controlled and uncontrolled.
    setValue(newValue ? newValue : null);

    // Make sure inputValue is always a string (not null or undefined), to avoid MUI console warnings.    
    setInputValue(typeof newValue === "object" ? newValue?.label || "" : newValue);
  }, [getValue, props.value])

  return (
    <Box m={1} className="view-edit-box">
      <Tooltip title={props.tooltip ?? ""}>
        <Autocomplete
          disablePortal
          options={options}
          clearOnBlur={true}
          readOnly={!props.isEdit}
          size={props.size ?? "small"}
          // If you don't use freeSolo, then if there's a saved "other" value, it has to be added to the options array,
          // or you'll get a MUI console warning about it not being a valid "options" value
          freeSolo={props.enableOther}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) => {
            let optionVal = typeof option === "object" ? option.id : option;
            let valueVal = typeof value === "object" ? value.id : value;
            return optionVal === valueVal;
          }}
          inputValue={inputValue}
          onInputChange={(event: any, newValue: string, reason: any) => {
            // inputValue is being managed explicitly, and saved with a useState object, to hold the value of the text typed by the user.
            // This way, the typed text for filtering options does not trigger onChange (it only triggers onInputChange), so we do not
            // update the actual value of the field, so clicking save while editing will not save random filtering input.

            // Prevent the reset by doing nothing.
            // This is done because the incident detail page reloads the incident object every 15 seconds, and that was causing the inputValue to reset.
            // If the user was editing while this happened, they would lose the text they had typed in to filter the options.
            if (reason === "reset" && event?.type !== "blur") {
              return;
            }

            // Otherwise, when the user is typing, or a click occurred to select or clear a value, then update the inputValue.
            setInputValue(newValue);
          }
          }
          value={value}
          onChange={(e: any, value: { id: string, label: string } | string | null, reason: any, details: any) => {
            // This event is triggered when a click occurs to select an option, or if the "x" is clicked to clear the value (and set it to an empty string).
            // This value can be the id & label object from one of the options, or a string.
            if (typeof value === "object") {
              props.handleInputChange(value?.id || "", props.id || "agency"); // Save just the ID.
              setValue(value); // Keep the object as the selected option.
            } else {
              props.handleInputChange(value, props.id || "agency");
              setValue(getValue(value));
            }
          }}

          id={props.id}
          renderInput={(params) => (
            <TextField
              required={props.required}
              {...params}
              id={props.id || "agency"}
              label={props.label || "Agency"}
              size={props.size ?? "small"}
              margin={"dense"}
              variant={props.isEdit ? "outlined" : "standard"}
              type="text"
              fullWidth
              InputLabelProps={{
                shrink: (props.isEdit && !props.disableShrinkLabel) || !!inputValue,
              }}
            />
          )}
        />
      </Tooltip>
    </Box>
  );
};

export default ViewEditAgencyAutocomplete;
